<template>
  <div
    class="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 px-4"
  >
    <div class="w-full max-w-md space-y-6">
      <!-- Encabezado -->
      <div class="text-center space-y-2">
        <div
          class="inline-flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 px-4 py-2 backdrop-blur-sm shadow-sm"
        >
          <span class="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
          <span
            class="text-[11px] uppercase tracking-[0.25em] text-slate-300"
          >
            Almacén
          </span>
        </div>

        <div>
          <h1 class="mt-4 text-3xl font-semibold text-slate-50">
            Crear cuenta
          </h1>
          <p class="mt-1 text-sm text-slate-400">
            Completa los datos para registrarte.
          </p>
        </div>
      </div>

      <!-- Tarjeta principal (glass) -->
      <div
        class="rounded-3xl bg-white/5 border border-white/10 shadow-[0_18px_45px_rgba(15,23,42,0.85)] backdrop-blur-2xl px-6 py-7 space-y-5"
      >
        <form @submit.prevent="handleRegister" class="space-y-4">
          <!-- Nombre -->
          <div class="space-y-2">
            <label
              class="block text-xs font-medium uppercase tracking-[0.18em] text-slate-300"
            >
              Tu nombre
            </label>
            <input
              v-model.trim="nombre"
              type="text"
              autocomplete="name"
              placeholder="Nombre y apellido"
              class="ios-input"
            />
          </div>

          <!-- Email -->
          <div class="space-y-2">
            <label
              class="block text-xs font-medium uppercase tracking-[0.18em] text-slate-300"
            >
              Correo electrónico
            </label>
            <input
              v-model.trim="email"
              type="email"
              autocomplete="email"
              placeholder="nombre@empresa.com"
              class="ios-input"
            />
          </div>

          <!-- Clave de autorización -->
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <label
                class="block text-xs font-medium uppercase tracking-[0.18em] text-slate-300"
              >
                Clave de autorización
              </label>
              <span
                v-if="claveLocked"
                class="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-400/30"
              >
                Desde enlace
              </span>
            </div>
            <input
              v-model="clave"
              type="text"
              maxlength="5"
              autocomplete="off"
              placeholder="XXXXX"
              class="ios-input"
              :readonly="claveLocked"
              :disabled="claveLocked"
              :class="{
                'opacity-70 cursor-not-allowed': claveLocked
              }"
              @input="onClaveInput"
            />
            <p class="text-[11px] text-slate-400">
              Código de 5 caracteres, siempre en mayúsculas.
            </p>
          </div>

          <!-- Contraseña -->
          <div class="space-y-2">
            <label
              class="block text-xs font-medium uppercase tracking-[0.18em] text-slate-300"
            >
              Contraseña
            </label>
            <input
              v-model="password"
              type="password"
              autocomplete="new-password"
              placeholder="Mínimo 10 caracteres"
              class="ios-input"
            />
            <p class="text-[11px] text-slate-400">
              Debe incluir al menos una mayúscula, una minúscula y un número.
            </p>
          </div>

          <!-- Confirmar contraseña -->
          <div class="space-y-2">
            <label
              class="block text-xs font-medium uppercase tracking-[0.18em] text-slate-300"
            >
              Confirmar contraseña
            </label>
            <input
              v-model="confirmPassword"
              type="password"
              autocomplete="new-password"
              placeholder="Repite tu contraseña"
              class="ios-input"
            />
          </div>

          <!-- Botón Continuar -->
          <button
            type="submit"
            class="ios-primary-button w-full flex items-center justify-center gap-2 mt-2"
            :disabled="alreadyRegistering"
          >
            <span v-if="!alreadyRegistering">Continuar</span>
            <span v-else class="loader-dot"></span>
          </button>
        </form>

        <!-- Términos -->
        <p class="mt-3 text-[11px] text-slate-400 text-center leading-relaxed">
          Al crear una cuenta aceptas las
          <a
            href="/condiciones-de-uso"
            class="underline underline-offset-2 decoration-slate-400 hover:text-slate-200 transition"
          >
            Condiciones de uso
          </a>
          y el
          <a
            href="/aviso-de-privacidad"
            class="underline underline-offset-2 decoration-slate-400 hover:text-slate-200 transition"
          >
            Aviso de privacidad
          </a>.
        </p>
      </div>

      <!-- Tarjeta: ya tienes cuenta -->
      <div
        class="rounded-3xl border border-white/10 bg-black/20 backdrop-blur-xl px-6 py-5 text-center shadow-[0_18px_45px_rgba(15,23,42,0.85)]"
      >
        <p class="text-sm text-slate-200 mb-3">
          ¿Ya tienes una cuenta?
        </p>
        <button
          type="button"
          class="ios-secondary-button w-full"
          @click="goToLogin"
        >
          Iniciar sesión
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { registerRequest } from './service/register.service';

export default {
  name: 'FormularioRegistrarse',

  props: {
    // Por si en algún punto el router ya te la pasa como prop
    claveProp: {
      type: String,
      default: '',
    },
  },

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      nombre: '',
      email: '',
      password: '',
      confirmPassword: '',
      clave: '',
      claveLocked: false,
      alreadyRegistering: false,
    };
  },

  mounted() {
    // Intentamos obtener la clave desde:
    // 1) prop
    // 2) query ?clave=XXXXX
    // 3) params /registrarse/XXXXX
    const fromProp = this.claveProp && this.claveProp.trim();
    const fromQuery = this.$route?.query?.clave;
    const fromParam = this.$route?.params?.clave;

    const rawClave = fromProp || fromQuery || fromParam;

    if (rawClave) {
      const normalized = String(rawClave)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 5);

      this.clave = normalized;
      this.claveLocked = true;
    }
  },

  methods: {
    onClaveInput() {
      if (this.claveLocked) return;

      this.clave = (this.clave || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 5);
    },

    validateEmail(email) {
      const re =
        /^([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
    },

    validatePassword(password) {
      // Mínimo 10 caracteres, al menos:
      // 1 mayúscula, 1 minúscula y 1 número
      if (!password || password.length < 10) return false;
      if (!/[A-Z]/.test(password)) return false;
      if (!/[a-z]/.test(password)) return false;
      if (!/[0-9]/.test(password)) return false;
      return true;
    },

    async handleRegister() {
      if (this.alreadyRegistering) return;

      const errors = [];

      if (!this.nombre.trim()) {
        errors.push('• Ingresa tu nombre.');
      }

      if (!this.validateEmail(this.email)) {
        errors.push('• Ingresa un correo electrónico válido.');
      }

      const claveTrim = (this.clave || '').trim();
      if (!claveTrim) {
        errors.push('• Ingresa la clave de autorización.');
      } else if (!/^[A-Z0-9]{5}$/.test(claveTrim)) {
        errors.push(
          '• La clave debe tener exactamente 5 caracteres en mayúsculas (letras o números).'
        );
      }

      if (!this.password) {
        errors.push('• Ingresa una contraseña.');
      } else if (!this.validatePassword(this.password)) {
        errors.push(
          '• La contraseña debe tener al menos 10 caracteres e incluir una mayúscula, una minúscula y un número.'
        );
      }

      if (this.password && this.confirmPassword !== this.password) {
        errors.push('• Las contraseñas no coinciden.');
      }

      if (errors.length > 0) {
        const message = errors.join('\n');
        this.showError(message);
        return;
      }

      this.alreadyRegistering = true;

      try {
        const payload = {
          nombre: this.nombre.trim(),
          email: this.email.trim(),
          password: this.password,
          clave: claveTrim,
        };

        const data = await registerRequest(payload);

        if (data && data.success) {
          this.showSuccess('Registro exitoso, ya puedes iniciar sesión.');
          this.$router.push('/login');
        } else {
          this.showError(
            data?.error || 'No se pudo completar el registro. Intenta de nuevo.'
          );
        }
      } catch (error) {
        console.error('Error de registro:', error);
        this.showError('Hubo un problema con tu solicitud de registro.');
      } finally {
        this.alreadyRegistering = false;
      }
    },

    goToLogin() {
      this.$router.push('/login');
    },

    showError(message) {
      if (this.toast) {
        this.toast.error(message, {
          timeout: 6000,
        });
      }
    },

    showSuccess(message) {
      if (this.toast) {
        this.toast.success(message, {
          timeout: 3000,
        });
      }
    },
  },
};
</script>

<style scoped>
.ios-input {
  width: 100%;
  border-radius: 1rem;
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #e5e7eb;
  outline: none;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease,
    transform 0.12s ease;
}

.ios-input::placeholder {
  color: rgba(148, 163, 184, 0.9);
}

.ios-input:focus {
  border-color: rgba(52, 211, 153, 0.95);
  box-shadow:
    0 0 0 1px rgba(52, 211, 153, 0.7),
    0 18px 45px rgba(15, 23, 42, 0.85);
  background: rgba(15, 23, 42, 0.98);
  transform: translateY(-1px);
}

.ios-primary-button {
  border-radius: 999px;
  padding: 0.7rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  background: linear-gradient(
    135deg,
    #f9fafb,
    #e5e7eb
  );
  color: #020617;
  border: none;
  box-shadow:
    0 12px 30px rgba(15, 23, 42, 0.7),
    0 0 0 1px rgba(148, 163, 184, 0.3);
  cursor: pointer;
  transition:
    transform 0.12s ease,
    box-shadow 0.18s ease,
    background 0.18s ease,
    opacity 0.18s ease;
}

.ios-primary-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow:
    0 16px 36px rgba(15, 23, 42, 0.85),
    0 0 0 1px rgba(148, 163, 184, 0.45);
}

.ios-primary-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow:
    0 10px 25px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(148, 163, 184, 0.35);
}

.ios-primary-button:disabled {
  opacity: 0.7;
  cursor: default;
}

.ios-secondary-button {
  border-radius: 999px;
  padding: 0.7rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  background: rgba(15, 23, 42, 0.8);
  color: #e5e7eb;
  border: 1px solid rgba(148, 163, 184, 0.6);
  cursor: pointer;
  transition:
    transform 0.12s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease,
    border-color 0.18s ease;
}

.ios-secondary-button:hover {
  background: rgba(15, 23, 42, 0.95);
  border-color: rgba(148, 163, 184, 0.9);
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.85);
}

.ios-secondary-button:active {
  transform: translateY(0);
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.9);
}

.loader-dot {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 999px;
  border: 2px solid rgba(15, 23, 42, 0.85);
  border-top-color: rgba(15, 23, 42, 0.1);
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
